import React, { useState, useEffect } from 'react';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, MessageCircle, TrendingUp, Search, Hash, Users, Flame, FileText, Play, Layers, Send, Image as ImageIcon, X, Sparkles, Sparkle, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import GifPicker from '@/components/GifPicker';
import YouTubeEmbed, { containsYouTubeUrl } from '@/components/YouTubeEmbed';
import { useAIFilter } from '@/context/AIFilterContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Explore = ({ user }) => {
  const navigate = useNavigate();
  const { hideAIContent } = useAIFilter();
  const [activeTab, setActiveTab] = useState('trending');
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ posts: [], users: [], hashtags: [] });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('exploreViewMode') || 'hybrid';
  });
  
  // Post creation state
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);

  useEffect(() => {
    if (activeTab === 'trending') {
      fetchTrending();
    } else if (activeTab === 'hashtags') {
      fetchHashtags();
    } else if (activeTab === 'people') {
      fetchSuggested();
    }
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('exploreViewMode', viewMode);
  }, [viewMode]);

  // Filter posts based on view mode and AI filter
  const filterPosts = (posts) => {
    let filtered = posts;
    
    // Filter AI content if enabled
    if (hideAIContent) {
      filtered = filtered.filter(p => !p.is_ai_generated && !p.ai_assisted && !p.ai_disclosure);
    }
    
    // Filter by view mode
    if (viewMode === 'text') {
      filtered = filtered.filter(p => !p.video_url && !p.youtube_url && !p.type?.includes('video'));
    } else if (viewMode === 'video') {
      filtered = filtered.filter(p => p.video_url || p.youtube_url || p.type?.includes('video'));
    }
    
    return filtered;
  };

  const fetchTrending = async () => {
    try {
      const response = await axios.get(`${API_URL}/trending/posts`, {
        withCredentials: true,
      });
      setTrendingPosts(response.data);
    } catch (error) {
      toast.error('Failed to load trending posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchHashtags = async () => {
    try {
      const response = await axios.get(`${API_URL}/trending/hashtags`, {
        withCredentials: true,
      });
      setTrendingHashtags(response.data);
    } catch (error) {
      toast.error('Failed to load hashtags');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggested = async () => {
    try {
      const response = await axios.get(`${API_URL}/trending/users`, {
        withCredentials: true,
      });
      setSuggestedUsers(response.data);
    } catch (error) {
      toast.error('Failed to load suggested users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const response = await axios.get(`${API_URL}/explore/search`, {
        params: { q: searchQuery, type: 'all' },
        withCredentials: true,
      });
      setSearchResults(response.data);
      setActiveTab('search');
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const followUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/users/follow/${userId}`, {}, {
        withCredentials: true,
      });
      setSuggestedUsers(suggestedUsers.filter(u => u.user_id !== userId));
      toast.success('Following!');
    } catch (error) {
      toast.error('Failed to follow');
    }
  };

  // Create post function
  const createPost = async () => {
    if (!newPost.trim() && !selectedGif && !selectedImage) return;
    setCreating(true);
    try {
      const detectedYouTubeUrl = containsYouTubeUrl(newPost);
      
      let imageUrl = null;
      
      // Upload image if selected
      if (selectedImage?.file) {
        const formData = new FormData();
        formData.append('file', selectedImage.file);
        const uploadResponse = await axios.post(
          `${API_URL}/posts/upload-image`,
          formData,
          { 
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        imageUrl = uploadResponse.data.url;
      }
      
      // Create the post
      const response = await axios.post(
        `${API_URL}/posts/`,
        { 
          content: newPost, 
          images: imageUrl ? [imageUrl] : [], 
          image_url: imageUrl,
          gif_url: selectedGif?.url || null, 
          youtube_url: detectedYouTubeUrl || null
        },
        { withCredentials: true }
      );
      
      toast.success('Post created!');
      
      // Add to trending posts if on trending tab
      if (activeTab === 'trending') {
        const userPost = { ...response.data, user: user, user_id: user.user_id, liked: false, engagement_score: 0 };
        setTrendingPosts([userPost, ...trendingPosts]);
      }
      
      // Reset form
      setNewPost('');
      setSelectedGif(null);
      setSelectedImage(null);
      setShowCreatePost(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 pb-32" data-testid="explore-page">
      {/* Small Search Bar - Fixed Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <div className="flex items-center gap-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-full shadow-lg border border-white/20 dark:border-white/10 px-3 py-2">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="bg-transparent border-none outline-none text-sm w-32 focus:w-48 transition-all duration-200 placeholder:text-gray-400 text-gray-700 dark:text-gray-200"
            data-testid="search-input"
          />
          {searchQuery && (
            <button
              onClick={handleSearch}
              className="p-1 bg-primary text-white rounded-full hover:bg-primary-700 transition-colors"
              data-testid="search-btn"
            >
              <Search className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent to-secondary flex items-center justify-center">
                <Flame className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-4xl text-gray-900 dark:text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 400 }} data-testid="explore-title">
                Socyy
              </h1>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 p-1 bg-white/70 dark:bg-gray-800/70 rounded-full shadow-lg border border-white/20 dark:border-white/10">
              <button
                onClick={() => setViewMode('text')}
                className={`p-2 rounded-full transition-all ${
                  viewMode === 'text'
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                }`}
                title="Text Only"
                data-testid="explore-view-mode-text"
              >
                <FileText className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('hybrid')}
                className={`p-2 rounded-full transition-all ${
                  viewMode === 'hybrid'
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                }`}
                title="All Posts (Hybrid)"
                data-testid="explore-view-mode-hybrid"
              >
                <Layers className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('video')}
                className={`px-3 py-2 rounded-full transition-all flex items-center gap-1 ${
                  viewMode === 'video'
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                }`}
                title="Vidyys Only"
                data-testid="explore-view-mode-video"
              >
                <Play className="w-4 h-4" />
                <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 400 }} className="text-sm">Vidyys</span>
              </button>
            </div>
          </div>

          {/* Create Post Button */}
          <div className="mb-6">
            <AnimatePresence mode="wait">
              {!showCreatePost ? (
                <motion.div
                  key="create-btn"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <Button
                    onClick={() => setShowCreatePost(true)}
                    className="w-full h-14 rounded-full bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90 text-white font-bold text-lg shadow-xl"
                    data-testid="create-post-btn"
                  >
                    <Plus className="w-6 h-6 mr-2" />
                    Create Post
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  key="create-form"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <Card className="p-5 rounded-2xl border border-white/20 shadow-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-md" data-testid="create-post-card">
                    <div className="flex gap-4">
                      <Avatar className="w-11 h-11 ring-2 ring-primary/20">
                        <AvatarImage src={user?.avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">{user?.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <Textarea
                          placeholder="What's happening?"
                          value={newPost}
                          onChange={(e) => setNewPost(e.target.value)}
                          className="rounded-xl border-2 resize-none mb-3 bg-gray-50 dark:bg-gray-700"
                          rows={3}
                          autoFocus
                          data-testid="create-post-input"
                        />
                        
                        {/* YouTube Preview */}
                        {containsYouTubeUrl(newPost) && (
                          <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-2">
                              <Play className="w-5 h-5" />
                              <span className="text-sm font-medium">YouTube video will be embedded</span>
                            </div>
                            <YouTubeEmbed url={containsYouTubeUrl(newPost)} />
                          </div>
                        )}
                        
                        {/* Selected GIF */}
                        {selectedGif && (
                          <div className="relative mb-3 inline-block">
                            <img src={selectedGif.url} alt="GIF" className="max-h-40 rounded-xl border" />
                            <button onClick={() => setSelectedGif(null)} className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        
                        {/* Selected Image */}
                        {selectedImage && (
                          <div className="relative mb-3 inline-block">
                            <img src={selectedImage.preview} alt="Selected" className="max-h-40 rounded-xl border" />
                            <button 
                              onClick={() => setSelectedImage(null)} 
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                              data-testid="remove-image-btn"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                      setSelectedImage({ file, preview: reader.result });
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                                className="sr-only"
                                data-testid="image-upload-input"
                              />
                              <Button variant="ghost" size="sm" className={`rounded-full ${selectedImage ? 'text-primary' : 'text-gray-600'}`} asChild>
                                <span><ImageIcon className="w-4 h-4 mr-1" /> Image</span>
                              </Button>
                            </label>
                            <div className="relative">
                              <Button variant="ghost" size="sm" onClick={() => setShowGifPicker(!showGifPicker)} className={`rounded-full ${selectedGif ? 'text-primary' : 'text-gray-600'}`}>
                                <Sparkles className="w-4 h-4 mr-1" /> GIF
                              </Button>
                              <AnimatePresence>
                                {showGifPicker && <GifPicker onSelect={(gif) => { setSelectedGif(gif); setShowGifPicker(false); }} onClose={() => setShowGifPicker(false)} />}
                              </AnimatePresence>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowCreatePost(false);
                                setNewPost('');
                                setSelectedGif(null);
                                setSelectedImage(null);
                              }}
                              className="rounded-full"
                              data-testid="cancel-post-btn"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={createPost}
                              disabled={creating || (!newPost.trim() && !selectedGif && !selectedImage)}
                              className="rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white font-bold px-6 shadow-lg"
                              data-testid="submit-post-btn"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              {creating ? 'Sending...' : 'Post'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 rounded-2xl p-1 bg-white/70 backdrop-blur-md mb-6">
            <TabsTrigger value="trending" className="rounded-xl font-bold" data-testid="tab-trending">
              <Flame className="w-4 h-4 mr-2" />
              Trending
            </TabsTrigger>
            <TabsTrigger value="hashtags" className="rounded-xl font-bold" data-testid="tab-hashtags">
              <Hash className="w-4 h-4 mr-2" />
              Hashtags
            </TabsTrigger>
            <TabsTrigger value="people" className="rounded-xl font-bold" data-testid="tab-people">
              <Users className="w-4 h-4 mr-2" />
              People
            </TabsTrigger>
            <TabsTrigger value="search" className="rounded-xl font-bold" data-testid="tab-search">
              <Search className="w-4 h-4 mr-2" />
              Search
            </TabsTrigger>
          </TabsList>

          <TabsContent value="trending">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : filterPosts(trendingPosts).length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center">
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  {trendingPosts.length === 0 
                    ? "No trending posts yet"
                    : viewMode === 'text' 
                      ? "No text posts to show. Try Hybrid mode."
                      : "No Vidyys to show. Try Hybrid mode."
                  }
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                {filterPosts(trendingPosts).map((post, idx) => (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card className="p-6 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md hover:shadow-2xl transition-all" data-testid={`trending-post-${idx}`}>
                      <div className="flex gap-4">
                        <Link to={`/profile/${post.user?.user_id}`}>
                          <Avatar className="w-12 h-12 cursor-pointer">
                            <AvatarImage src={post.user?.avatar} />
                            <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                              {post.user?.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                        </Link>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h3 className="font-bold text-gray-900 dark:text-white">{post.user?.name}</h3>
                              <p className="text-sm text-gray-500">@{post.user?.username}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Post Type Badge */}
                              {post.post_type && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  post.post_type === 'video' || post.post_type === 'video_text' 
                                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                    : post.post_type === 'image' || post.post_type === 'image_text'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                    : post.post_type === 'gif' || post.post_type === 'gif_text'
                                    ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                }`} style={post.post_type === 'video' || post.post_type === 'video_text' ? { fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic' } : {}}>
                                  {post.post_type === 'video' ? 'Vidyy' 
                                    : post.post_type === 'video_text' ? 'Vidyy + Text'
                                    : post.post_type === 'image' ? 'Image'
                                    : post.post_type === 'image_text' ? 'Image + Text'
                                    : post.post_type === 'gif' ? 'GIF'
                                    : post.post_type === 'gif_text' ? 'GIF + Text'
                                    : 'Text'}
                                </span>
                              )}
                              <TrendingUp className="w-4 h-4 text-accent" />
                              <span className="text-sm text-gray-500">
                                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                          
                          {/* Post Content */}
                          {post.content && (
                            <p className="text-gray-800 dark:text-gray-200 mb-3 whitespace-pre-wrap">{post.content}</p>
                          )}
                          
                          {/* YouTube Video */}
                          {post.youtube_url && (
                            <div className="mb-3 rounded-xl overflow-hidden">
                              <YouTubeEmbed url={post.youtube_url} />
                            </div>
                          )}
                          
                          {/* Video */}
                          {post.video_url && (
                            <div className="relative rounded-xl overflow-hidden bg-black mb-3">
                              <video
                                src={`${process.env.REACT_APP_BACKEND_URL}${post.video_url}`}
                                className="w-full max-h-96 object-contain"
                                controls
                              />
                            </div>
                          )}
                          
                          {/* GIF */}
                          {post.gif_url && (
                            <img src={post.gif_url} alt="GIF" className="max-h-72 rounded-xl border mb-3" />
                          )}
                          
                          {/* Images */}
                          {post.images && post.images.length > 0 && (
                            <div className={`grid gap-2 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} mb-3`}>
                              {post.images.map((img, i) => (
                                <img key={i} src={img} alt="" className="rounded-xl w-full object-cover max-h-64" />
                              ))}
                            </div>
                          )}
                          
                          {/* Audio */}
                          {post.audio_url && (
                            <div className="mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800">
                              <audio
                                src={`${process.env.REACT_APP_BACKEND_URL}${post.audio_url}`}
                                controls
                                className="w-full h-10"
                                preload="metadata"
                              />
                            </div>
                          )}
                          
                          {/* Single image_url fallback */}
                          {post.image_url && (!post.images || post.images.length === 0) && (
                            <img src={post.image_url} alt="" className="rounded-xl w-full object-cover max-h-64 mb-3" />
                          )}
                          
                          {/* Actions */}
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-1 text-red-500">
                              <Heart className={`w-5 h-5 ${post.liked ? 'fill-current' : ''}`} />
                              <span className="font-bold">{post.likes_count || 0}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                              <MessageCircle className="w-5 h-5" />
                              <span>{post.comments_count || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="hashtags">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : trendingHashtags.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md text-center">
                <p className="text-lg text-gray-600">No trending hashtags yet</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {trendingHashtags.map((tag, idx) => (
                  <motion.div
                    key={tag.hashtag}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card
                      className="p-6 rounded-3xl border border-white/20 shadow-xl bg-gradient-to-br from-white to-primary-50 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer"
                      onClick={() => {
                        setSearchQuery(`#${tag.hashtag}`);
                        handleSearch();
                      }}
                      data-testid={`hashtag-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Hash className="w-5 h-5 text-primary" />
                            <h3 className="text-xl font-heading font-bold text-gray-900">{tag.hashtag}</h3>
                          </div>
                          <p className="text-sm text-gray-600">{tag.count} posts</p>
                        </div>
                        <TrendingUp className="w-6 h-6 text-accent" />
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="people">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : suggestedUsers.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md text-center">
                <p className="text-lg text-gray-600">No suggestions available</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {suggestedUsers.map((suggestedUser, idx) => (
                  <motion.div
                    key={suggestedUser.user_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card className="p-6 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md hover:shadow-2xl transition-all" data-testid={`suggested-user-${idx}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Link to={`/profile/${suggestedUser.user_id}`}>
                            <Avatar className="w-16 h-16 cursor-pointer">
                              <AvatarImage src={suggestedUser.avatar} />
                              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-xl font-bold">
                                {suggestedUser.name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                          </Link>
                          <div>
                            <h3 className="font-bold text-lg text-gray-900">{suggestedUser.name}</h3>
                            <p className="text-sm text-gray-500 mb-1">@{suggestedUser.username}</p>
                            <p className="text-sm text-gray-600">{suggestedUser.followers_count} followers</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => followUser(suggestedUser.user_id)}
                          className="rounded-full bg-primary hover:bg-primary-700 text-white font-bold px-6 shadow-lg hover:scale-105 transition-transform"
                          data-testid={`follow-btn-${idx}`}
                        >
                          Follow
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="search">
            {searchResults.posts.length === 0 && searchResults.users.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md text-center">
                <p className="text-lg text-gray-600">No results found. Try a different search term.</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {searchResults.users.length > 0 && (
                  <div>
                    <h3 className="text-xl font-heading font-bold text-gray-900 mb-4">People</h3>
                    <div className="space-y-4">
                      {searchResults.users.map((searchUser) => (
                        <Card key={searchUser.user_id} className="p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md">
                          <Link to={`/profile/${searchUser.user_id}`} className="flex items-center gap-3">
                            <Avatar className="w-12 h-12">
                              <AvatarImage src={searchUser.avatar} />
                              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                                {searchUser.name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h4 className="font-bold text-gray-900">{searchUser.name}</h4>
                              <p className="text-sm text-gray-500">@{searchUser.username}</p>
                            </div>
                          </Link>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.posts.length > 0 && (
                  <div>
                    <h3 className="text-xl font-heading font-bold text-gray-900 mb-4">Posts</h3>
                    <div className="space-y-4">
                      {searchResults.posts.map((post) => (
                        <Card key={post.post_id} className="p-6 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md">
                          <div className="flex gap-4">
                            <Avatar className="w-12 h-12">
                              <AvatarImage src={post.user?.avatar} />
                              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                                {post.user?.name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900">{post.user?.name}</h4>
                              <p className="text-gray-800 mt-2">{post.content}</p>
                              <div className="flex items-center gap-4 mt-3 text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Heart className="w-4 h-4" />
                                  <span>{post.likes_count}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <MessageCircle className="w-4 h-4" />
                                  <span>{post.comments_count}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <FloatingDock user={user} />
    </div>
  );
};

export default Explore;